import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search as SearchIcon, Edit, X, Save, Trash2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Header from '@/components/Header';
import { DELIVERY_OPTIONS, deliveryLabel } from '@/lib/deliveryModes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchAdvertiser = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [editingAdvertiser, setEditingAdvertiser] = useState(null);
  const [duplicatingAdvertiser, setDuplicatingAdvertiser] = useState(null);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [role, setRole] = useState('');
  const [sortColumn, setSortColumn] = useState('advertiser_id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchCriteria, setSearchCriteria] = useState({
    magazine: '',
    advertiser_id: '',
    name: '',
    company_name: '',
    email: '',
    status: '',
    district: '',
    state: '',
    contact_no: ''
  });

  useEffect(() => {
    fetchStates();
    const storedRole = localStorage.getItem('role');
    setRole(storedRole || '');
  }, []);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/all-unique-states`);
      setStates(response.data.states);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/advertisers/search`, searchCriteria);
      setSearchResults(response.data);
      
      if (response.data.length === 0) {
        toast.info('No advertisers found matching the criteria');
      } else {
        toast.success(`Found ${response.data.length} advertiser(s)`);
      }
    } catch (error) {
      console.error('Error searching advertisers:', error);
      toast.error('Failed to search advertisers');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedResults = () => {
    if (!sortColumn) return searchResults;

    return [...searchResults].sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';

      // Special handling for advertiser_id - extract numeric part
      if (sortColumn === 'advertiser_id') {
        const extractNumber = (id) => {
          const match = id.match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        };
        aVal = extractNumber(aVal);
        bVal = extractNumber(bVal);
      }
      // Handle date columns
      else if (sortColumn === 'created_at' || sortColumn === 'updated_at' || sortColumn === 'end_date') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      // Handle numeric columns
      else if (sortColumn === 'amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      // Handle string columns
      else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleReset = () => {
    setSearchCriteria({
      magazine: '',
      advertiser_id: '',
      name: '',
      company_name: '',
      email: '',
      status: '',
      district: '',
      state: '',
      contact_no: ''
    });
    setSearchResults([]);
    setSortColumn('');
    setSortDirection('asc');
    setDistricts([]);
  };

  const fetchDistricts = async (state) => {
    if (!state) {
      setDistricts([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/all-unique-districts/${encodeURIComponent(state)}`);
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  const handleEdit = (advertiser) => {
    // Ensure default values for status and delivery_mode if not present
    const advertiserForEdit = {
      ...advertiser,
      status: advertiser.status || 'Active',
      delivery_mode: deliveryLabel(advertiser.delivery_mode) || 'Registered Post'
    };
    setEditingAdvertiser(advertiserForEdit);
    // Fetch districts for the advertiser's state
    if (advertiser.state) {
      fetchDistricts(advertiser.state);
    }
  };

  const handleCancelEdit = () => {
    setEditingAdvertiser(null);
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data matching AdvertiserCreate model
      const updateData = {
        name: editingAdvertiser.name,
        initials: editingAdvertiser.initials,
        designation: editingAdvertiser.designation,
        company_name: editingAdvertiser.company_name,
        contact_no: editingAdvertiser.contact_no,
        whatsapp_no: editingAdvertiser.whatsapp_no || '',
        email: editingAdvertiser.email,
        address: editingAdvertiser.address,
        pincode: editingAdvertiser.pincode,
        state: editingAdvertiser.state,
        district: editingAdvertiser.district,
        size: editingAdvertiser.size,
        duration: editingAdvertiser.duration,
        amount: editingAdvertiser.amount,
        payment_status: editingAdvertiser.payment_status,
        status: editingAdvertiser.status,
        start_date: editingAdvertiser.start_date || '',
        end_date: editingAdvertiser.end_date || '',
        delivery_mode: editingAdvertiser.delivery_mode,
        notes: editingAdvertiser.notes,
        print_schedule_type: editingAdvertiser.print_schedule_type,
        schedule_type: editingAdvertiser.schedule_type || '',
        magazine: editingAdvertiser.magazine
      };
      
      await axios.put(`${API}/advertisers/${editingAdvertiser.advertiser_id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Advertiser updated successfully');
      setEditingAdvertiser(null);
      handleSearch({ preventDefault: () => {} });
    } catch (error) {
      console.error('Error updating advertiser:', error);
      toast.error('Failed to update advertiser: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (advertiserId, advertiserName) => {
    if (!window.confirm(`Are you sure you want to delete advertiser "${advertiserName}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/advertisers/${advertiserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Advertiser deleted successfully');
      // Refresh search results
      setSearchResults(searchResults.filter(a => a.advertiser_id !== advertiserId));
    } catch (error) {
      console.error('Error deleting advertiser:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to delete advertiser');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (advertiser) => {
    // Fetch the new advertiser ID first
    try {
      const response = await axios.get(`${API}/advertisers/next-id`, {
        params: {
          magazine: advertiser.magazine || 'Aabkari Times'
        }
      });
      
      // Create a copy of the advertiser data for duplication with new ID
      const advertiserForDuplicate = {
        ...advertiser,
        new_advertiser_id: response.data.next_id,
        name: advertiser.name,
        company_name: advertiser.company_name,
        status: advertiser.status || 'Active',
        delivery_mode: deliveryLabel(advertiser.delivery_mode) || 'Registered Post',
        magazine: advertiser.magazine || 'Aabkari Times',
        gst_no: advertiser.gst_no || '',
        po_wo_no: advertiser.po_wo_no || ''
      };
      setDuplicatingAdvertiser(advertiserForDuplicate);
      // Fetch districts for the advertiser's state
      if (advertiser.state) {
        fetchDistricts(advertiser.state);
      }
    } catch (error) {
      console.error('Error fetching next advertiser ID:', error);
      toast.error('Failed to initialize duplicate. Please try again.');
    }
  };

  // Fetch new ID when magazine changes in duplicate modal
  const fetchNewIdForDuplicate = async (magazine) => {
    try {
      const response = await axios.get(`${API}/advertisers/next-id`, {
        params: { magazine: magazine }
      });
      setDuplicatingAdvertiser(prev => ({ ...prev, new_advertiser_id: response.data.next_id }));
    } catch (error) {
      console.error('Error fetching next advertiser ID:', error);
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicatingAdvertiser(null);
  };

  const handleSaveDuplicate = async () => {
    if (!duplicatingAdvertiser.name) {
      toast.error('Name is required');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data for creating new advertiser (duplicate)
      const duplicateData = {
        name: duplicatingAdvertiser.name,
        initials: duplicatingAdvertiser.initials || '',
        designation: duplicatingAdvertiser.designation || '',
        company_name: duplicatingAdvertiser.company_name || '',
        contact_no: duplicatingAdvertiser.contact_no || '',
        email: duplicatingAdvertiser.email || '',
        address: duplicatingAdvertiser.address || '',
        pincode: duplicatingAdvertiser.pincode || '',
        state: duplicatingAdvertiser.state || '',
        district: duplicatingAdvertiser.district || '',
        size: duplicatingAdvertiser.size || '',
        duration: duplicatingAdvertiser.duration || '',
        amount: duplicatingAdvertiser.amount || '',
        payment_status: duplicatingAdvertiser.payment_status || '',
        status: duplicatingAdvertiser.status || 'Active',
        start_date: duplicatingAdvertiser.start_date || '',
        end_date: duplicatingAdvertiser.end_date || '',
        delivery_mode: deliveryLabel(duplicatingAdvertiser.delivery_mode) || 'Registered Post',
        notes: duplicatingAdvertiser.notes || '',
        print_schedule_type: duplicatingAdvertiser.print_schedule_type || '',
        schedule_type: duplicatingAdvertiser.schedule_type || '',
        magazine: duplicatingAdvertiser.magazine,
        gst_no: duplicatingAdvertiser.gst_no || '',
        po_wo_no: duplicatingAdvertiser.po_wo_no || ''
      };
      
      await axios.post(`${API}/advertisers`, duplicateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Advertiser duplicated successfully! New ID: ${duplicatingAdvertiser.new_advertiser_id}`);
      setDuplicatingAdvertiser(null);
      handleSearch({ preventDefault: () => {} });
    } catch (error) {
      console.error('Error duplicating advertiser:', error);
      toast.error('Failed to duplicate advertiser: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const years = Array.from({ length: 10 }, (_, i) => (new Date().getFullYear() + i).toString());

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center space-x-4 mb-8">
          <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
            <ArrowLeft className="mr-2" size={18} />
            Back
          </Button>
          <h1 className="text-4xl font-bold" style={{ color: '#059669' }} data-testid="search-title">
            Search Advertiser
          </h1>
        </div>

        <form onSubmit={handleSearch} className="form-section mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Magazine</Label>
              <Select value={searchCriteria.magazine} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, magazine: value })}>
                <SelectTrigger data-testid="magazine-filter">
                  <SelectValue placeholder="Select magazine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={searchCriteria.status} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, status: value })}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Advertiser ID</Label>
              <Input data-testid="advertiser-id-input" value={searchCriteria.advertiser_id} onChange={(e) => setSearchCriteria({ ...searchCriteria, advertiser_id: e.target.value })} placeholder="Enter advertiser ID" />
            </div>

            <div className="space-y-2">
              <Label>Name</Label>
              <Input data-testid="name-input" value={searchCriteria.name} onChange={(e) => setSearchCriteria({ ...searchCriteria, name: e.target.value })} placeholder="Enter name" />
            </div>

            <div className="space-y-2">
              <Label>Mobile Number</Label>
              <Input data-testid="mobile-input" value={searchCriteria.contact_no} onChange={(e) => setSearchCriteria({ ...searchCriteria, contact_no: e.target.value })} placeholder="Enter mobile number (partial)" />
            </div>

            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input data-testid="company-name-input" value={searchCriteria.company_name} onChange={(e) => setSearchCriteria({ ...searchCriteria, company_name: e.target.value })} placeholder="Enter company name" />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="email-input" value={searchCriteria.email} onChange={(e) => setSearchCriteria({ ...searchCriteria, email: e.target.value })} placeholder="Enter email" />
            </div>

            <div className="space-y-2">
              <Label>State</Label>
              <Select value={searchCriteria.state || 'all_states'} onValueChange={(value) => {
                const newState = value === 'all_states' ? '' : value;
                setSearchCriteria({ ...searchCriteria, state: newState, district: '' });
                setDistricts([]);
                if (newState) fetchDistricts(newState);
              }}>
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_states">All States</SelectItem>
                  {states.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>District</Label>
              <Select value={searchCriteria.district || 'all_districts'} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, district: value === 'all_districts' ? '' : value })} disabled={!searchCriteria.state}>
                <SelectTrigger data-testid="district-select">
                  <SelectValue placeholder={searchCriteria.state ? 'All Districts' : 'Select state first'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_districts">All Districts</SelectItem>
                  {districts.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-8 flex space-x-4">
            <Button type="submit" disabled={loading} className="btn-primary" style={{ backgroundColor: '#059669' }} data-testid="search-button">
              <SearchIcon className="mr-2" size={18} />
              {loading ? 'Searching...' : 'Search'}
            </Button>
            <Button type="button" onClick={handleReset} variant="outline" data-testid="reset-button">
              Reset
            </Button>
          </div>
        </form>

        {/* Edit Modal */}
        {editingAdvertiser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ overflow: 'auto' }}>
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold" style={{ color: '#059669' }}>Edit Advertiser</h2>
                <Button variant="outline" onClick={handleCancelEdit} size="sm">
                  <X size={18} />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Advertiser ID</Label>
                  <Input value={editingAdvertiser.advertiser_id} disabled />
                </div>

                <div className="space-y-2">
                  <Label>Created Date</Label>
                  <Input value={editingAdvertiser.created_at ? new Date(editingAdvertiser.created_at).toLocaleString('en-IN') : 'N/A'} disabled className="bg-gray-100" />
                </div>

                <div className="space-y-2">
                  <Label>Last Modified Date</Label>
                  <Input value={editingAdvertiser.updated_at ? new Date(editingAdvertiser.updated_at).toLocaleString('en-IN') : 'N/A'} disabled className="bg-gray-100" />
                </div>

                <div className="space-y-2">
                  <Label>Initials</Label>
                  <Input value={editingAdvertiser.initials} onChange={(e) => setEditingAdvertiser({ ...editingAdvertiser, initials: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={editingAdvertiser.name} onChange={(e) => setEditingAdvertiser({ ...editingAdvertiser, name: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input value={editingAdvertiser.designation} onChange={(e) => setEditingAdvertiser({ ...editingAdvertiser, designation: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input value={editingAdvertiser.company_name} onChange={(e) => setEditingAdvertiser({ ...editingAdvertiser, company_name: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Contact</Label>
                  <Input value={editingAdvertiser.contact_no} onChange={(e) => setEditingAdvertiser({ ...editingAdvertiser, contact_no: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>WhatsApp Number</Label>
                  <Input 
                    value={editingAdvertiser.whatsapp_no || ''} 
                    onChange={(e) => setEditingAdvertiser({ ...editingAdvertiser, whatsapp_no: e.target.value.replace(/\D/g, '').slice(-10) })} 
                    placeholder="10-digit WhatsApp number"
                    maxLength={10}
                  />
                  <p className="text-xs text-gray-500">Country code +91 assumed</p>
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="text" value={editingAdvertiser.email} onChange={(e) => setEditingAdvertiser({ ...editingAdvertiser, email: e.target.value })} placeholder="Multiple emails separated by comma" />
                  <p className="text-xs text-gray-500">Comma-separated emails allowed</p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Address</Label>
                  <Textarea value={editingAdvertiser.address} onChange={(e) => setEditingAdvertiser({ ...editingAdvertiser, address: e.target.value })} rows={2} />
                </div>

                <div className="space-y-2">
                  <Label>State</Label>
                  <Select 
                    value={editingAdvertiser.state} 
                    onValueChange={(value) => {
                      setEditingAdvertiser({ ...editingAdvertiser, state: value, district: '' });
                      fetchDistricts(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>District</Label>
                  <Input 
                    list="districts-edit-advertiser"
                    value={editingAdvertiser.district} 
                    onChange={(e) => setEditingAdvertiser({ ...editingAdvertiser, district: e.target.value })} 
                    placeholder="Select or type district"
                  />
                  <datalist id="districts-edit-advertiser">
                    {districts.map((district) => (
                      <option key={district} value={district} />
                    ))}
                  </datalist>
                </div>

                <div className="space-y-2">
                  <Label>Pincode (6 digits)</Label>
                  <Input 
                    type="text"
                    maxLength={6}
                    value={editingAdvertiser.pincode ? String(editingAdvertiser.pincode).replace('.0', '') : ''} 
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                      setEditingAdvertiser({ ...editingAdvertiser, pincode: value });
                    }} 
                  />
                </div>

                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input value={editingAdvertiser.amount} onChange={(e) => setEditingAdvertiser({ ...editingAdvertiser, amount: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={editingAdvertiser.status || 'Active'} 
                    onValueChange={(value) => setEditingAdvertiser({ ...editingAdvertiser, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Delivery</Label>
                  <Select 
                    value={deliveryLabel(editingAdvertiser.delivery_mode) || 'Registered Post'} 
                    onValueChange={(value) => setEditingAdvertiser({ ...editingAdvertiser, delivery_mode: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select delivery method" />
                    </SelectTrigger>
                    <SelectContent>
                      {DELIVERY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Notes</Label>
                  <Textarea value={editingAdvertiser.notes} onChange={(e) => setEditingAdvertiser({ ...editingAdvertiser, notes: e.target.value })} rows={2} />
                </div>
              </div>

              <div className="mt-8 flex justify-end space-x-4">
                <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={loading} style={{ backgroundColor: '#059669', color: 'white' }}>
                  <Save className="mr-2" size={18} />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Duplicate Modal - Full Form */}
        {duplicatingAdvertiser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ overflow: 'auto' }}>
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold" style={{ color: '#059669' }}>Duplicate Advertiser</h2>
                <Button variant="outline" onClick={handleCancelDuplicate} size="sm">
                  <X size={18} />
                </Button>
              </div>

              {/* Info Banner */}
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-green-700">
                      <strong>Original Advertiser ID:</strong> {duplicatingAdvertiser.advertiser_id}
                    </p>
                    <p className="text-sm text-green-700">
                      <strong>Original Name:</strong> {duplicatingAdvertiser.name}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-600">
                      New ID: {duplicatingAdvertiser.new_advertiser_id || 'Calculating...'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Magazine */}
                <div className="space-y-2">
                  <Label>Magazine *</Label>
                  <Select 
                    value={duplicatingAdvertiser.magazine || 'Aabkari Times'} 
                    onValueChange={(value) => {
                      setDuplicatingAdvertiser({ ...duplicatingAdvertiser, magazine: value });
                      fetchNewIdForDuplicate(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                      <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Initials */}
                <div className="space-y-2">
                  <Label>Initials</Label>
                  <Input value={duplicatingAdvertiser.initials || ''} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, initials: e.target.value })} />
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={duplicatingAdvertiser.name} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, name: e.target.value })} />
                </div>

                {/* Designation */}
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input value={duplicatingAdvertiser.designation || ''} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, designation: e.target.value })} />
                </div>

                {/* Company */}
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input value={duplicatingAdvertiser.company_name || ''} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, company_name: e.target.value })} />
                </div>

                {/* Contact */}
                <div className="space-y-2">
                  <Label>Contact</Label>
                  <Input value={duplicatingAdvertiser.contact_no || ''} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, contact_no: e.target.value })} />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="text" value={duplicatingAdvertiser.email || ''} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, email: e.target.value })} placeholder="Multiple emails separated by comma" />
                </div>

                {/* GST No */}
                <div className="space-y-2">
                  <Label>GST No.</Label>
                  <Input value={duplicatingAdvertiser.gst_no || ''} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, gst_no: e.target.value })} placeholder="GST Number" />
                </div>

                {/* PO/WO No */}
                <div className="space-y-2">
                  <Label>PO/WO No.</Label>
                  <Input value={duplicatingAdvertiser.po_wo_no || ''} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, po_wo_no: e.target.value })} placeholder="PO/WO Number" />
                </div>

                {/* Address - Full Width */}
                <div className="space-y-2 md:col-span-2 lg:col-span-3">
                  <Label>Address</Label>
                  <Textarea value={duplicatingAdvertiser.address || ''} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, address: e.target.value })} rows={2} />
                </div>

                {/* State */}
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select 
                    value={duplicatingAdvertiser.state || 'none'} 
                    onValueChange={(value) => {
                      const stateVal = value === 'none' ? '' : value;
                      setDuplicatingAdvertiser({ ...duplicatingAdvertiser, state: stateVal, district: '' });
                      if (stateVal) fetchDistricts(stateVal);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select --</SelectItem>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* District */}
                <div className="space-y-2">
                  <Label>District</Label>
                  <Input 
                    list="districts-duplicate-advertiser"
                    value={duplicatingAdvertiser.district || ''} 
                    onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, district: e.target.value })} 
                    placeholder="Select or type district"
                  />
                  <datalist id="districts-duplicate-advertiser">
                    {districts.map((district) => (
                      <option key={district} value={district} />
                    ))}
                  </datalist>
                </div>

                {/* Pincode */}
                <div className="space-y-2">
                  <Label>Pincode</Label>
                  <Input 
                    type="text"
                    maxLength={6}
                    value={duplicatingAdvertiser.pincode ? String(duplicatingAdvertiser.pincode).replace('.0', '') : ''} 
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setDuplicatingAdvertiser({ ...duplicatingAdvertiser, pincode: value });
                    }} 
                  />
                </div>

                {/* Ad Size */}
                <div className="space-y-2">
                  <Label>Ad Size</Label>
                  <Input value={duplicatingAdvertiser.size || ''} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, size: e.target.value })} placeholder="e.g., Full Page, Half Page" />
                </div>

                {/* Duration */}
                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Input value={duplicatingAdvertiser.duration || ''} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, duration: e.target.value })} placeholder="e.g., 1 Year, 6 Months" />
                </div>

                {/* Amount */}
                <div className="space-y-2">
                  <Label>Amount (₹)</Label>
                  <Input type="number" value={duplicatingAdvertiser.amount || ''} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, amount: e.target.value })} />
                </div>

                {/* Payment Status */}
                <div className="space-y-2">
                  <Label>Payment Status</Label>
                  <Select 
                    value={duplicatingAdvertiser.payment_status || 'none'} 
                    onValueChange={(value) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, payment_status: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select --</SelectItem>
                      <SelectItem value="Paid">Paid</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Status */}
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={duplicatingAdvertiser.status || 'Active'} 
                    onValueChange={(value) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Delivery Mode */}
                <div className="space-y-2">
                  <Label>Delivery</Label>
                  <Select 
                    value={deliveryLabel(duplicatingAdvertiser.delivery_mode) || 'Registered Post'} 
                    onValueChange={(value) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, delivery_mode: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DELIVERY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes - Full Width */}
                <div className="space-y-2 md:col-span-2 lg:col-span-3">
                  <Label>Notes</Label>
                  <Textarea value={duplicatingAdvertiser.notes || ''} onChange={(e) => setDuplicatingAdvertiser({ ...duplicatingAdvertiser, notes: e.target.value })} rows={2} />
                </div>
              </div>

              <div className="mt-6 pt-4 border-t flex justify-end space-x-4">
                <Button variant="outline" onClick={handleCancelDuplicate}>Cancel</Button>
                <Button onClick={handleSaveDuplicate} disabled={loading} style={{ backgroundColor: '#059669', color: 'white' }}>
                  <Copy className="mr-2" size={18} />
                  {loading ? 'Creating...' : `Create as ${duplicatingAdvertiser.new_advertiser_id || 'New Advertiser'}`}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="form-section">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#059669' }}>
              Search Results ({searchResults.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="search-results-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('advertiser_id')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      ID {sortColumn === 'advertiser_id' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Name {sortColumn === 'name' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('company_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Company {sortColumn === 'company_name' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Status {sortColumn === 'status' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('end_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      End Date {sortColumn === 'end_date' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('contact_no')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Contact No. {sortColumn === 'contact_no' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('whatsapp_no')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      WhatsApp No. {sortColumn === 'whatsapp_no' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Email {sortColumn === 'email' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedResults().map((advertiser) => (
                    <tr key={advertiser.id} data-testid={`advertiser-row-${advertiser.advertiser_id}`}>
                      <td>{advertiser.advertiser_id}</td>
                      <td>{advertiser.name}</td>
                      <td>{advertiser.company_name}</td>
                      <td>{advertiser.status}</td>
                      <td>{advertiser.end_month} {advertiser.end_year}</td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '120px' }}>
                        {advertiser.contact_no || '-'}
                      </td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '120px' }}>
                        {advertiser.whatsapp_no || '-'}
                      </td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '180px', fontSize: '12px' }}>
                        {advertiser.email || '-'}
                      </td>
                      <td>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(advertiser)} data-testid={`edit-${advertiser.advertiser_id}`}>
                            <Edit size={16} className="mr-1" />
                            Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDuplicate(advertiser)} data-testid={`duplicate-${advertiser.advertiser_id}`}>
                            <Copy size={16} className="mr-1" />
                            Duplicate
                          </Button>
                          {role === 'admin' && (
                            <Button 
                              size="sm" 
                              variant="destructive" 
                              onClick={() => handleDelete(advertiser.advertiser_id, advertiser.name)} 
                              data-testid={`delete-${advertiser.advertiser_id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchAdvertiser;